import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetadataRunConditionDto } from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';
import { AutoSizeInput } from '@/Domain/components/Shared/AutosizeInput/AutosizeInput';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  METADATA_CONDITION_VALUE_MAX_LENGTH,
  METADATA_CONDITION_VALUES_MAX_COUNT,
  metadataConditionUsesValue,
} from '@/Domain/helper/metadataRunCondition.helper';

type Props = Pick<MetadataRunConditionDto, 'operator' | 'value' | 'values'> & {
  id: string;
  onChange: (operand: Pick<MetadataRunConditionDto, 'value' | 'values'>) => void;
  error?: string;
};

export const MetadataConditionValue = ({
  id,
  operator,
  value,
  values = [],
  onChange,
  error,
}: Props) => {
  const { t } = useTranslation('metadata_conditions');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showEmptyValue, setShowEmptyValue] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  useEffect(() => {
    if (focusIndex === null) return;
    inputRefs.current[focusIndex]?.focus();
    setFocusIndex(null);
  }, [focusIndex]);

  if (operator !== 'is_one_of' && !metadataConditionUsesValue(operator)) return null;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : undefined;
  const atLimit = values.length >= METADATA_CONDITION_VALUES_MAX_COUNT;
  const canAddValue =
    !atLimit && !showEmptyValue && values.length > 0 && values.every((entry) => entry.length > 0);
  const addValue = (): void => {
    if (!canAddValue) return;
    setShowEmptyValue(true);
    setFocusIndex(values.length);
  };
  const removeValue = (index: number): void => {
    onChange({ value: null, values: values.filter((_, itemIndex) => index !== itemIndex) });
    setFocusIndex(Math.max(0, index - 1));
  };

  return (
    <Field data-invalid={!!error} className="min-w-0">
      <FieldLabel htmlFor={id} className="sr-only">
        {t(operator === 'is_one_of' ? 'values' : 'value')}
      </FieldLabel>
      {operator === 'is_one_of' ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {(values.length && !showEmptyValue ? values : [...values, '']).map((entry, index) => (
            <Badge
              key={index}
              variant="secondary"
              size="lg"
              className="box-border h-auto min-w-0 max-w-full focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
            >
              <AutoSizeInput
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                value={entry}
                onChange={(nextValue) => {
                  const next = [...values];
                  next[index] = nextValue;
                  onChange({ value: null, values: next });
                  setShowEmptyValue(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addValue();
                  }
                }}
                onBlur={() => {
                  if (!entry.length && index === values.length) setShowEmptyValue(false);
                }}
                inputProps={{
                  id: index === 0 ? id : `${id}-${index}`,
                  'data-testid': `${id}-item-${index}`,
                  'aria-label': t('value_number', { index: index + 1 }),
                  'aria-describedby': describedBy,
                  'aria-invalid': !!error,
                  placeholder: t('value_placeholder'),
                  maxLength: METADATA_CONDITION_VALUE_MAX_LENGTH,
                  autoComplete: 'off',
                  style: { width: entry ? `${Math.min(40, entry.length + 1)}ch` : '14ch' },
                }}
              />
              {index < values.length && (
                <IconButton
                  variant="ghost"
                  size="xxs"
                  onClick={() => removeValue(index)}
                  aria-label={t('remove_value', { index: index + 1 })}
                  data-testid={`${id}-remove-${index}`}
                >
                  <X />
                </IconButton>
              )}
            </Badge>
          ))}
          <Button
            variant="secondary"
            size="xs"
            onClick={addValue}
            disabled={!canAddValue}
            data-testid={`${id}-add`}
          >
            <Plus data-icon="inline-start" />
            {t('add_value')}
          </Button>
        </div>
      ) : (
        <Input
          id={id}
          data-testid={id}
          size="sm"
          value={value ?? ''}
          maxLength={METADATA_CONDITION_VALUE_MAX_LENGTH}
          error={!!error}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          placeholder={t('value_placeholder')}
          onChange={(event) => onChange({ value: event.target.value, values: [] })}
        />
      )}
      {(value?.length === METADATA_CONDITION_VALUE_MAX_LENGTH ||
        values.some((entry) => entry.length === METADATA_CONDITION_VALUE_MAX_LENGTH)) && (
        <FieldDescription data-testid={`${id}-length-limit`}>
          {t('value_length_limit', { limit: METADATA_CONDITION_VALUE_MAX_LENGTH })}
        </FieldDescription>
      )}
      {atLimit && (
        <FieldDescription data-testid={`${id}-count-limit`}>
          {t('values_limit', { limit: METADATA_CONDITION_VALUES_MAX_COUNT })}
        </FieldDescription>
      )}
      {error && (
        <FieldError id={errorId} data-testid={errorId}>
          {error}
        </FieldError>
      )}
    </Field>
  );
};
